import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    GameOverWrapper,
    GameOverTitleWrapper,
    GameOverMatchupWrapper,
    GameOverLineScoreWrapper,
    GameOverDecisionsWrapper,
    GameOverLinkWrapper,
    ViewWrapButton,
    TiebreakerButton,
    TiebreakerLink
} from './GameOver.style';
import { withTheme } from 'styled-components';
import { useDecoratorTranslate } from '@gameday/utils/hooks/useDecoratorTranslate';
import LineScoreDataContainer from '../LineScore/containers/LineScoreDataContainer';
import TeamSummaryMatchupDataContainer from '../TeamSummaryMatchup/TeamSummaryMatchupDataContainer';
import DecisionsDataContainer from '../Decisions/containers/DecisionsDataContainer';
import VoteButtonDataContainer from '../PlayerOfTheGame/containers/VoteButtonDataContainer';
import {
    ButtonShapes,
    ButtonSizes
} from '@mlb/react-patterns/v4/components/Button/button.types';
import useTracking from '@gameday/utils/hooks/useTracking';
import { gameOverScenarios } from './store/selectors/getGameOverState';
import { resourcePath } from '@gameday/utils/resource-path';

const potgTrackingProps = {
    componentName: ['Live']
};

const customFormat = {
    showHeadShot: true,
    useDecisionBox: false,
    headshotWidth: '48px'
};

const { WINNER_LOSER, END_IN_TIE, END_IN_TIEBREAKER, ACTIVE_TIEBREAKER } = gameOverScenarios;
const trackingConfig = ['Live', 'TeamSummaryMatchup'];

const GameOver = ({
    wrapButtonHandler,
    teamNameLabels,
    gameOverScenario,
    tiebreakerInfo
}) => {
    const tracking = useTracking();
    const translate = useDecoratorTranslate();
    const viewWrapText = translate('button_view_the_wrap', 'View the Wrap');
    const playerVotingText = translate('button_player_of_the_game_voting', 'Player of the Game Voting');

    const { scenario } = gameOverScenario;

    const displayDecisions = scenario === WINNER_LOSER;

    const topText = { key: '', text: '' };
    let showTiebreakerButton = false;
    let showViewWrapButton = true;

    switch (scenario) {
        case WINNER_LOSER:
            topText.key = 'label_team_win';
            topText.text = `${teamNameLabels.winnerAbbreviation} wins!`;
            break;
        case END_IN_TIE:
            topText.key = 'label_team_tie';
            topText.text = 'Tie Game';
            break;
        case END_IN_TIEBREAKER:
            topText.key = 'label_tiebreaker_winner';
            topText.text = `${teamNameLabels.winnerAbbreviation} wins tiebreaker`;
            showTiebreakerButton = tiebreakerInfo?.settingValue === 'homeRunDerby' && tiebreakerInfo?.xrefId;
            break;
        case ACTIVE_TIEBREAKER:
            topText.key = 'label_tiebreaker_text';
            topText.text = 'Tiebreaker';
            showTiebreakerButton = tiebreakerInfo?.settingValue === 'homeRunDerby' && tiebreakerInfo?.xrefId;
            showViewWrapButton = false;
            break;
    }

    const modalTitle = translate(topText.key, topText.text, teamNameLabels);
    const tiebreakerLink = showTiebreakerButton && resourcePath.get('homeRunDerby', tiebreakerInfo?.xrefId);
    const viewTiebreakerText = translate('button_view_tiebreaker', 'View Tiebreaker');

    const viewWrapHandlers = useMemo(() => {
        return {
            onClick: (e) => {
                const text = e?.target.innerText;
                const cfg = {
                    componentName: ['Game Over'],
                    text,
                    activity: 'View Wrap Click'
                };
                tracking?.track.track_click(cfg);
                if (wrapButtonHandler) {
                    wrapButtonHandler();
                }
            }
        };
    }, [wrapButtonHandler]);

    return (
        <GameOverWrapper>
            <GameOverTitleWrapper>
                { modalTitle }
            </GameOverTitleWrapper>
            <GameOverMatchupWrapper>
                <TeamSummaryMatchupDataContainer
                    displayRecords={ false }
                    displayStandings={ false }
                    hideTeamNames
                    isLive
                    mobileWidth
                    componentName={trackingConfig}
                />
            </GameOverMatchupWrapper>
            <GameOverLineScoreWrapper>
                <LineScoreDataContainer />
            </GameOverLineScoreWrapper>
            {displayDecisions && (
                <GameOverDecisionsWrapper>
                    <DecisionsDataContainer
                        customFormat={customFormat}
                    />
                </GameOverDecisionsWrapper>
            )}
            {showViewWrapButton && (
                <GameOverLinkWrapper>
                    <ViewWrapButton
                        shape={ButtonShapes.PILL_SOLID}
                        label={viewWrapText}
                        handlers={viewWrapHandlers}
                    />
                </GameOverLinkWrapper>
            )}
            {showTiebreakerButton && (
                <GameOverLinkWrapper>
                    <TiebreakerLink url={tiebreakerLink} target={'_blank'}>
                        <TiebreakerButton
                            shape={ButtonShapes.PILL}
                            label={viewTiebreakerText}
                            iconName={'arrow-next'}
                            iconLeft={false}
                            size={ButtonSizes.XLARGE}
                        />
                    </TiebreakerLink>
                </GameOverLinkWrapper>
            )}
            <VoteButtonDataContainer trackingProps={potgTrackingProps} label={playerVotingText} componentName='Game Over' />
        </GameOverWrapper>
    );
};

GameOver.propTypes = {
    wrapButtonHandler: PropTypes.func,
    teamNameLabels: PropTypes.object,
    gameOverScenario: PropTypes.object,
    tiebreakerInfo: PropTypes.object
};

export default withTheme(GameOver);
